import {
  Key,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BellOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  EyeOutlined,
  FolderOutlined,
  MailOutlined,
  MoreOutlined,
  PlusOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  TeamOutlined,
  UserOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Input, Modal, Table, Tabs, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TablePaginationConfig } from 'antd/es/table';
import type {
  FilterValue,
  SorterResult,
  TableRowSelection,
} from 'antd/es/table/interface';
import { useProfile } from '../../profile/components/ProfileContext';
import {
  InvitedUser as ModelInvitedUser,
  UserId as ModelUser,
} from '../models/users.model';
import { UsersService } from '../services/users.service';
import { UtilService } from '../../../../../core/services/util.service';
import { route } from '../../../../../constants/routes';

const userInitials = (user: Pick<ModelUser, 'firstname' | 'lastname'>) =>
  `${user.firstname?.[0] || ''}${user.lastname?.[0] || ''}`.toUpperCase() ||
  'US';

const userName = (user: Pick<ModelUser, 'firstname' | 'lastname'>) =>
  `${user.firstname || ''} ${user.lastname || ''}`.trim() || 'Unnamed user';

const roleLabel = (user: ModelUser) => {
  if (user.admin) return 'Company admin';
  const role = user.roles?.[0];
  if (!role) return 'Recruiter';
  return role
    .replace(/^ROLE_/, '')
    .toLowerCase()
    .split('_')
    .map((part) => `${part[0]?.toUpperCase() || ''}${part.slice(1)}`)
    .join(' ');
};

const UsersTable = forwardRef<{ refresh: () => Promise<void> }>((_, ref) => {
  const { companyId, isAdmin, profile } = useProfile();
  const navigate = useNavigate();
  const [activeData, setActiveData] = useState<ModelUser[]>([]);
  const [archivedData, setArchivedData] = useState<ModelUser[]>([]);
  const [invitedData, setInvitedData] = useState<ModelInvitedUser[]>([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [activeTotal, setActiveTotal] = useState(0);
  const [archivedTotal, setArchivedTotal] = useState(0);
  const [invitedTotal, setInvitedTotal] = useState(0);
  const [filter, setFilter] = useState<string>();
  const [sort, setSort] = useState<string>();
  const [activeTab, setActiveTab] = useState<'active' | 'invited' | 'archived'>(
    'active'
  );
  const [loading, setLoading] = useState(false);
  const [bulkArchiving, setBulkArchiving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<ModelUser | null>(null);
  const [selectedUserLoading, setSelectedUserLoading] = useState(false);
  const [selectedUserDetailsUnavailable, setSelectedUserDetailsUnavailable] =
    useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<Key[]>([]);
  const countsFetchedRef = useRef(false);
  const didInitialLoad = useRef(false);
  const previewRequestRef = useRef(0);

  const fetchTabData = useCallback(
    async (
      tabType: 'active' | 'invited' | 'archived',
      fetchDataToo: boolean = true
    ) => {
      if (!companyId) return;
      if (fetchDataToo) setLoading(true);

      try {
        if (tabType === 'invited') {
          const result = await UsersService.GetInvitedUsers(companyId);
          const invitedUsers = result || [];
          setInvitedData(invitedUsers);
          setInvitedTotal(invitedUsers.length);
          return;
        }

        const deletedFilter =
          tabType === 'archived' ? 'deleted:eq:true' : 'deleted:eq:false';
        const combinedFilter = filter
          ? `${filter},${deletedFilter}`
          : deletedFilter;
        const params = fetchDataToo
          ? {
              page: pagination.current,
              size: pagination.pageSize,
              ...(sort ? { sort } : {}),
              filter: combinedFilter,
            }
          : {
              page: 1,
              size: 1,
              filter: deletedFilter,
            };
        const result = await UsersService.GetUsers(companyId, params);
        const content = result?.content || [];
        const total = result?.totalElements || 0;

        if (fetchDataToo) {
          if (tabType === 'active') {
            setActiveData(content);
            setActiveTotal(total);
          } else {
            setArchivedData(content);
            setArchivedTotal(total);
          }
        } else if (tabType === 'active') {
          setActiveTotal(total);
        } else {
          setArchivedTotal(total);
        }
      } catch (error) {
        console.error(`Failed to fetch ${tabType} users:`, error);
        UtilService.Alert(
          'Users unavailable',
          'error',
          'We could not load this part of the team directory. Please try again.'
        );
      } finally {
        if (fetchDataToo) setLoading(false);
      }
    },
    [companyId, filter, pagination, sort]
  );

  const fetchInitialData = useCallback(async () => {
    if (!companyId || countsFetchedRef.current) return;
    await Promise.all([
      fetchTabData('active', true),
      fetchTabData('archived', false),
      fetchTabData('invited', true),
    ]);
    countsFetchedRef.current = true;
  }, [companyId, fetchTabData]);

  useEffect(() => {
    if (companyId && !countsFetchedRef.current && !didInitialLoad.current) {
      void fetchInitialData();
      didInitialLoad.current = true;
    }
  }, [companyId, fetchInitialData]);

  useEffect(() => {
    if (didInitialLoad.current && companyId && countsFetchedRef.current) {
      void fetchTabData(activeTab, true);
    }
  }, [activeTab, companyId, fetchTabData, filter, sort]);

  useImperativeHandle(ref, () => ({
    refresh: async () => {
      setPagination({ current: 1, pageSize: 10 });
      setSort(undefined);
      setFilter(undefined);
      setSearchTerm('');
      previewRequestRef.current += 1;
      setSelectedUser(null);
      setSelectedUserLoading(false);
      setSelectedUserDetailsUnavailable(false);
      setSelectedUserIds([]);
      countsFetchedRef.current = false;
      didInitialLoad.current = false;
      await fetchInitialData();
      didInitialLoad.current = true;
    },
  }));

  const getCurrentUsers = useCallback(
    () => (activeTab === 'archived' ? archivedData : activeData),
    [activeData, activeTab, archivedData]
  );

  const visibleUsers = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) return getCurrentUsers();
    return getCurrentUsers().filter((user) =>
      [
        user.firstname,
        user.lastname,
        user.email,
        user.phoneNumber,
        roleLabel(user),
      ].some((value) => value?.toLowerCase().includes(normalized))
    );
  }, [getCurrentUsers, searchTerm]);

  const visibleInvitedUsers = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();
    if (!normalized) return invitedData;
    return invitedData.filter((user) =>
      [user.firstname, user.lastname, user.email].some((value) =>
        value?.toLowerCase().includes(normalized)
      )
    );
  }, [invitedData, searchTerm]);

  const administratorsOnPage = activeData.filter((user) => user.admin).length;

  const addNew = () => navigate(route('/user-profile'));

  const viewUser = (user: ModelUser) => {
    navigate(route(`/user-profile/${user.id}`), {
      state: {
        userName: userName(user),
        deleted: activeTab === 'archived',
      },
    });
  };

  const openUserPreview = async (user: ModelUser) => {
    if (!companyId) return;
    const requestId = previewRequestRef.current + 1;
    previewRequestRef.current = requestId;
    setSelectedUser(user);
    setSelectedUserLoading(true);
    setSelectedUserDetailsUnavailable(false);

    try {
      const details = await UsersService.GetById(companyId, user.id);
      if (previewRequestRef.current !== requestId) return;
      setSelectedUser({ ...user, ...details });
    } catch (error) {
      if (previewRequestRef.current !== requestId) return;
      console.error('Failed to load user profile details:', error);
      setSelectedUserDetailsUnavailable(true);
    } finally {
      if (previewRequestRef.current === requestId) {
        setSelectedUserLoading(false);
      }
    }
  };

  const closeUserPreview = () => {
    previewRequestRef.current += 1;
    setSelectedUser(null);
    setSelectedUserLoading(false);
    setSelectedUserDetailsUnavailable(false);
  };

  const archiveUser = async (user: ModelUser) => {
    if (!companyId) return;
    try {
      await UsersService.Delete(companyId, user.id);
      UtilService.Alert(
        'User archived',
        'success',
        `"${userName(user)}" has been moved to Archived.`,
        undefined,
        async () => {
          closeUserPreview();
          setSelectedUserIds([]);
          await Promise.all([
            fetchTabData('active', true),
            fetchTabData('archived', false),
          ]);
        }
      );
    } catch (error) {
      console.error('Error archiving user:', error);
      UtilService.Alert(
        'Archive unavailable',
        'error',
        'The user could not be archived. Please try again.'
      );
    }
  };

  const confirmArchive = (user: ModelUser) => {
    Modal.confirm({
      title: `Archive ${userName(user)}?`,
      content:
        'This user will lose active workspace access and remain available under Archived.',
      okText: 'Archive user',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      onOk: () => archiveUser(user),
    });
  };

  const onChange = (
    paginationConfig: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ModelUser> | SorterResult<ModelUser>[]
  ) => {
    const filterParts = Object.entries(filters).flatMap(([key, values]) => {
      const value = values?.[0];
      return value ? [`${key}:like:${value}`] : [];
    });
    setFilter(filterParts.length ? filterParts.join(',') : undefined);

    if (!Array.isArray(sorter) && sorter.order) {
      if (sorter.field === 'admin') {
        const sortedData = [...getCurrentUsers()].sort((a, b) =>
          sorter.order === 'ascend'
            ? Number(a.admin) - Number(b.admin)
            : Number(b.admin) - Number(a.admin)
        );
        if (activeTab === 'active') setActiveData(sortedData);
        if (activeTab === 'archived') setArchivedData(sortedData);
        setSort(undefined);
      } else {
        const direction = sorter.order === 'ascend' ? '' : '-';
        setSort(`${direction}${sorter.field}`);
      }
    } else {
      setSort(undefined);
    }

    setPagination({
      current: paginationConfig.current || 1,
      pageSize: paginationConfig.pageSize || 10,
    });
  };

  const columns: ColumnsType<ModelUser> = [
    {
      title: 'User',
      dataIndex: 'firstname',
      key: 'firstname',
      sorter: true,
      width: 230,
      render: (_, user) => (
        <div className="company-directory__identity-cell">
          <span className="company-directory__avatar" aria-hidden>
            {userInitials(user)}
          </span>
          <span>
            <strong>{userName(user)}</strong>
            <small>{user.email || 'Email not provided'}</small>
          </span>
        </div>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'admin',
      key: 'admin',
      sorter: true,
      width: 150,
      render: (_, user) => roleLabel(user),
    },
    {
      title: 'Contact',
      key: 'contact',
      width: 160,
      render: (_, user) => (
        <div className="company-directory__contact-links">
          {user.email ? (
            <a href={`mailto:${user.email}`}>
              <MailOutlined aria-hidden />
              Email
            </a>
          ) : null}
          {user.phoneNumber ? (
            <a
              href={`https://wa.me/${user.phoneNumber.replace(/\D/g, '')}`}
              target="_blank"
              rel="noreferrer"
            >
              <WhatsAppOutlined aria-hidden />
              WhatsApp
            </a>
          ) : (
            <span className="company-directory__muted">No phone</span>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_, user) => (
        <Tag
          className={`company-directory__status company-directory__status--${
            user.deleted ? 'archived' : user.enabled ? 'active' : 'attention'
          }`}
        >
          {user.deleted ? 'Archived' : user.enabled ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      width: 90,
      render: (_, user) => {
        const canArchive =
          isAdmin && activeTab === 'active' && profile?.id !== user.id;
        const items = [
          {
            key: 'view',
            icon: <EyeOutlined />,
            label: 'View profile',
          },
          ...(canArchive
            ? [
                {
                  key: 'archive',
                  icon: <FolderOutlined />,
                  label: 'Archive user',
                  danger: true,
                },
              ]
            : []),
        ];
        return (
          <Dropdown
            trigger={['click']}
            menu={{
              items,
              onClick: ({ key, domEvent }) => {
                domEvent.stopPropagation();
                if (key === 'view') viewUser(user);
                if (key === 'archive') confirmArchive(user);
              },
            }}
          >
            <Button
              type="text"
              className="company-directory__more"
              aria-label={`Actions for ${userName(user)}`}
              icon={<MoreOutlined />}
              onClick={(event) => event.stopPropagation()}
            />
          </Dropdown>
        );
      },
    },
  ];

  const invitedColumns: ColumnsType<ModelInvitedUser> = [
    {
      title: 'Invited user',
      key: 'user',
      render: (_, user) => (
        <div className="company-directory__identity-cell">
          <span className="company-directory__avatar" aria-hidden>
            {`${user.firstname?.[0] || ''}${
              user.lastname?.[0] || ''
            }`.toUpperCase() || 'IN'}
          </span>
          <span>
            <strong>
              {`${user.firstname || ''} ${user.lastname || ''}`.trim() ||
                'Pending user'}
            </strong>
            <small>{user.email}</small>
          </span>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 140,
      render: () => (
        <Tag className="company-directory__status company-directory__status--attention">
          Invitation pending
        </Tag>
      ),
    },
  ];

  const rowSelection: TableRowSelection<ModelUser> = {
    selectedRowKeys: selectedUserIds,
    onChange: setSelectedUserIds,
    preserveSelectedRowKeys: true,
    getCheckboxProps: (user) => ({
      disabled: activeTab === 'active' && isAdmin && profile?.id === user.id,
      title:
        activeTab === 'active' && isAdmin && profile?.id === user.id
          ? 'You cannot archive your own account'
          : undefined,
    }),
  };

  const selectedUsers = getCurrentUsers().filter((user) =>
    selectedUserIds.includes(user.id)
  );
  const selectedEmails = selectedUsers
    .map((user) => user.email)
    .filter(Boolean)
    .join(',');
  const archivableSelectedUsers = selectedUsers.filter(
    (user) => activeTab === 'active' && !user.deleted && user.id !== profile?.id
  );

  const archiveSelectedUsers = async () => {
    if (!companyId || !archivableSelectedUsers.length) return;
    setBulkArchiving(true);
    try {
      const results = await Promise.allSettled(
        archivableSelectedUsers.map((user) =>
          UsersService.Delete(companyId, user.id)
        )
      );
      const archivedCount = results.filter(
        (result) => result.status === 'fulfilled'
      ).length;
      const failedCount = results.length - archivedCount;

      closeUserPreview();
      setSelectedUserIds([]);
      await Promise.all([
        fetchTabData('active', true),
        fetchTabData('archived', false),
      ]);

      if (!failedCount) {
        UtilService.Alert(
          'Users archived',
          'success',
          `${archivedCount} user${archivedCount === 1 ? '' : 's'} ${
            archivedCount === 1 ? 'has' : 'have'
          } been moved to Archived.`
        );
      } else if (archivedCount) {
        UtilService.Alert(
          'Archive partially completed',
          'warning',
          `${archivedCount} archived and ${failedCount} failed. Refresh and try the remaining users again.`
        );
      } else {
        UtilService.Alert(
          'Archive unavailable',
          'error',
          'None of the selected users could be archived. Please try again.'
        );
      }
    } catch (error) {
      console.error('Error archiving selected users:', error);
      UtilService.Alert(
        'Archive unavailable',
        'error',
        'The selected users could not be archived. Please try again.'
      );
    } finally {
      setBulkArchiving(false);
    }
  };

  const confirmArchiveSelected = () => {
    if (!archivableSelectedUsers.length) return;
    const count = archivableSelectedUsers.length;
    Modal.confirm({
      title: `Archive ${count} user${count === 1 ? '' : 's'}?`,
      content:
        'They will lose active workspace access and remain available under Archived. Your own account is protected.',
      okText: `Archive ${count}`,
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      onOk: archiveSelectedUsers,
    });
  };

  const searchControl = (
    <div className="company-directory__toolbar">
      <Input
        allowClear
        prefix={<SearchOutlined aria-hidden />}
        placeholder="Search this page by name, email, or role"
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
      />
      <span className="company-directory__result-count" aria-live="polite">
        {activeTab === 'invited'
          ? visibleInvitedUsers.length
          : visibleUsers.length}{' '}
        shown
      </span>
    </div>
  );

  const renderUsersTable = (label: string) => (
    <section
      className="company-directory__workspace"
      aria-label={`${label} users`}
    >
      {searchControl}
      <Table<ModelUser>
        className="company-directory__table"
        columns={columns}
        dataSource={visibleUsers}
        rowKey="id"
        rowSelection={rowSelection}
        loading={loading || bulkArchiving}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: activeTab === 'active' ? activeTotal : archivedTotal,
          showSizeChanger: true,
          showTotal: (total) => `${total} users`,
          onChange: (current, pageSize) => setPagination({ current, pageSize }),
          onShowSizeChange: (current, pageSize) =>
            setPagination({ current, pageSize }),
        }}
        onChange={onChange}
        onRow={(user) => ({
          onClick: () => void openUserPreview(user),
        })}
        rowClassName={(user) =>
          selectedUser?.id === user.id
            ? 'company-directory__row--previewed'
            : ''
        }
        scroll={{ x: 720 }}
      />

      {selectedUserIds.length ? (
        <div className="company-directory__bulk" role="status">
          <strong>
            {selectedUserIds.length} user
            {selectedUserIds.length === 1 ? '' : 's'} selected
          </strong>
          {selectedEmails ? (
            <a href={`mailto:${selectedEmails}`}>
              <MailOutlined aria-hidden />
              Email
            </a>
          ) : null}
          {selectedUsers.length === 1 ? (
            <button type="button" onClick={() => viewUser(selectedUsers[0])}>
              <EyeOutlined aria-hidden />
              Open profile
            </button>
          ) : null}
          {isAdmin &&
          activeTab === 'active' &&
          archivableSelectedUsers.length ? (
            <button
              className="company-directory__bulk-danger"
              type="button"
              disabled={bulkArchiving}
              onClick={confirmArchiveSelected}
            >
              <FolderOutlined aria-hidden />
              {bulkArchiving ? 'Archiving…' : 'Archive selected'}
            </button>
          ) : null}
          <button type="button" onClick={() => setSelectedUserIds([])}>
            <CloseOutlined aria-hidden />
            Clear
          </button>
        </div>
      ) : null}
    </section>
  );

  const tabItems = [
    {
      key: 'active',
      label: (
        <span>
          Active <b>{activeTotal}</b>
        </span>
      ),
      children: renderUsersTable('Active'),
    },
    {
      key: 'invited',
      label: (
        <span>
          Invited <b>{invitedTotal}</b>
        </span>
      ),
      children: (
        <section
          className="company-directory__workspace"
          aria-label="Invited users"
        >
          {searchControl}
          <Table<ModelInvitedUser>
            className="company-directory__table"
            columns={invitedColumns}
            dataSource={visibleInvitedUsers}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: invitedTotal,
              showSizeChanger: true,
              showTotal: (total) => `${total} invitations`,
              onChange: (current, pageSize) =>
                setPagination({ current, pageSize }),
              onShowSizeChange: (current, pageSize) =>
                setPagination({ current, pageSize }),
            }}
            scroll={{ x: 600 }}
          />
        </section>
      ),
    },
    {
      key: 'archived',
      label: (
        <span>
          Archived <b>{archivedTotal}</b>
        </span>
      ),
      children: renderUsersTable('Archived'),
    },
  ];

  return (
    <div className="company-directory">
      <header className="company-directory__header">
        <div>
          <span>Team &amp; access</span>
          <h2>Users</h2>
          <p>Manage workspace access, roles, and notification preferences.</p>
        </div>
        {isAdmin ? (
          <button
            className="company-primary-action"
            type="button"
            onClick={addNew}
          >
            <PlusOutlined aria-hidden />
            Invite user
          </button>
        ) : null}
      </header>

      <section className="company-directory__metrics" aria-label="User totals">
        <article>
          <span className="company-directory__metric-icon">
            <TeamOutlined aria-hidden />
          </span>
          <div>
            <span>Total users</span>
            <strong>{activeTotal + archivedTotal}</strong>
          </div>
        </article>
        <article>
          <span className="company-directory__metric-icon">
            <CheckCircleOutlined aria-hidden />
          </span>
          <div>
            <span>Active</span>
            <strong>{activeTotal}</strong>
          </div>
        </article>
        <article>
          <span className="company-directory__metric-icon">
            <SafetyCertificateOutlined aria-hidden />
          </span>
          <div>
            <span>Admins on page</span>
            <strong>{administratorsOnPage}</strong>
          </div>
        </article>
        <article>
          <span className="company-directory__metric-icon company-directory__metric-icon--warning">
            <BellOutlined aria-hidden />
          </span>
          <div>
            <span>Pending invites</span>
            <strong>{invitedTotal}</strong>
          </div>
        </article>
      </section>

      <div
        className={`company-directory__layout${
          selectedUser ? ' company-directory__layout--preview' : ''
        }`}
      >
        <section className="company-directory__panel">
          <Tabs
            activeKey={activeTab}
            items={tabItems}
            onChange={(key) => {
              setActiveTab(key as 'active' | 'invited' | 'archived');
              setPagination((current) => ({ ...current, current: 1 }));
              setSearchTerm('');
              closeUserPreview();
              setSelectedUserIds([]);
            }}
          />
        </section>

        {selectedUser ? (
          <aside
            className="company-directory__preview"
            aria-label="User preview"
          >
            <div className="company-directory__preview-header">
              <strong>User preview</strong>
              <Button
                type="text"
                icon={<CloseOutlined />}
                aria-label="Close user preview"
                onClick={closeUserPreview}
              />
            </div>
            <div className="company-directory__preview-body">
              <div className="company-directory__preview-identity">
                <span className="company-directory__preview-avatar" aria-hidden>
                  {userInitials(selectedUser)}
                </span>
                <div>
                  <h3>{userName(selectedUser)}</h3>
                  <span>{roleLabel(selectedUser)}</span>
                  <Tag
                    className={`company-directory__status company-directory__status--${
                      selectedUser.deleted
                        ? 'archived'
                        : selectedUser.enabled
                          ? 'active'
                          : 'attention'
                    }`}
                  >
                    {selectedUser.deleted
                      ? 'Archived'
                      : selectedUser.enabled
                        ? 'Active'
                        : 'Inactive'}
                  </Tag>
                </div>
              </div>

              {selectedUserLoading ? (
                <div
                  className="company-directory__preview-loading"
                  role="status"
                >
                  Loading user details…
                </div>
              ) : selectedUserDetailsUnavailable ? (
                <div
                  className="company-directory__preview-loading"
                  role="status"
                >
                  User details are temporarily unavailable.
                </div>
              ) : null}

              <section className="company-directory__preview-section">
                <h4>Contact</h4>
                <strong>{selectedUser.email || 'Email not provided'}</strong>
                <span>{selectedUser.phoneNumber || 'Phone not provided'}</span>
              </section>

              <div className="company-directory__preview-actions">
                {selectedUser.email ? (
                  <a href={`mailto:${selectedUser.email}`}>
                    <MailOutlined aria-hidden />
                    Email
                  </a>
                ) : null}
                {selectedUser.phoneNumber ? (
                  <a
                    href={`https://wa.me/${selectedUser.phoneNumber.replace(
                      /\D/g,
                      ''
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <WhatsAppOutlined aria-hidden />
                    WhatsApp
                  </a>
                ) : null}
              </div>

              <section className="company-directory__preview-section">
                <h4>Access</h4>
                <ul className="company-directory__access-list">
                  <li>
                    <SafetyCertificateOutlined aria-hidden />
                    {roleLabel(selectedUser)}
                  </li>
                  <li>
                    <UserOutlined aria-hidden />
                    {selectedUser.admin
                      ? 'Company administration'
                      : 'Recruiting workspace'}
                  </li>
                </ul>
              </section>

              <section className="company-directory__preview-section">
                <h4>Notifications</h4>
                {selectedUserLoading ? (
                  <span>Loading preferences…</span>
                ) : selectedUserDetailsUnavailable ? (
                  <span>Notification preferences could not be loaded.</span>
                ) : (
                  <dl className="company-directory__details">
                    <div>
                      <dt>Email</dt>
                      <dd>
                        {selectedUser.emailNotifiable ? 'Enabled' : 'Disabled'}
                      </dd>
                    </div>
                    <div>
                      <dt>WhatsApp</dt>
                      <dd>
                        {selectedUser.whatsappNotifiable
                          ? 'Enabled'
                          : 'Disabled'}
                      </dd>
                    </div>
                  </dl>
                )}
              </section>
            </div>
            <button
              className="company-primary-action company-directory__preview-primary"
              type="button"
              onClick={() => viewUser(selectedUser)}
            >
              Manage user
            </button>
          </aside>
        ) : null}
      </div>
    </div>
  );
});

export default UsersTable;
